import React, { useState, useEffect } from "react";
import {
  Layout,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Divider,
  message,
  Switch,
} from "antd";
import {
  CloseOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import PanelVisualization from "../panels/PanelVisualization";
import { dashboardApi } from "../../services/dashboardApi";
import ThresholdEditor from "./ThresholdEditor";
import {
  transformPrometheusData,
  parseGrafanaTimeToUnix,
} from "../../utils/dataTransformers";
import { replacePanelVariables } from "../../utils/variableReplacer";
import PanelOptionsEditor from "./PanelOptionsEditor";
import LabelValueSelector from './LabelValueSelector';
import { useTheme } from '../../context/ThemeContext';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

// Parse PromQL expr to extract metric and label filters for builder mode
const parsePromQLToBuilder = (expr) => {
  if (!expr || typeof expr !== "string") return { metric: "", filters: [] };
  const trimmed = expr.trim();
  const braceIdx = trimmed.indexOf("{");
  let metric = trimmed;
  let filterStr = "";
  if (braceIdx >= 0) {
    metric = trimmed.slice(0, braceIdx).trim();
    const closeIdx = trimmed.indexOf("}", braceIdx);
    if (closeIdx >= 0) filterStr = trimmed.slice(braceIdx + 1, closeIdx);
  }
  const filters = [];
  if (filterStr) {
    const parts = filterStr.split(",").map((p) => p.trim()).filter(Boolean);
    for (const part of parts) {
      const eqMatch = part.match(/^(\w+)(=~|!=~|=|!=)"([^"]*)"$/);
      if (eqMatch) {
        filters.push({
          label: eqMatch[1],
          operator: eqMatch[2],
          value: eqMatch[3],
          values: [],
        });
      }
    }
  }
  return { metric, filters };
};

const STEP_OPTIONS = [
  { value: "15s", label: "15s" },
  { value: "30s", label: "30s" },
  { value: "60s", label: "1m" },
  { value: "120s", label: "2m" },
  { value: "300s", label: "5m" },
];

const PanelEditor = ({
  panel,
  timeRange,
  variables = {},
  variableDefinitions = [],
  onClose,
  onSave,
  availableMetrics = [],
}) => {
  const { isDark } = useTheme();
  // Panel config state
  const [editedPanel, setEditedPanel] = useState(() => {
    const p = { ...panel };
    if (!p.targets?.length) {
      p.targets = [{ refId: "A", expr: "", legendFormat: "" }];
    }
    return p;
  });
  const [queryMode, setQueryMode] = useState("builder");
  const [showThresholds, setShowThresholds] = useState(false);

  // Query builder state - init from panel
  const [selectedMetric, setSelectedMetric] = useState(() => {
    const expr = panel.targets?.[0]?.expr;
    const { metric } = parsePromQLToBuilder(expr);
    return metric || "";
  });
  const [labelFilters, setLabelFilters] = useState(() => {
    const expr = panel.targets?.[0]?.expr;
    const { filters } = parsePromQLToBuilder(expr);
    return filters.length ? filters : [];
  });
  const [availableLabels, setAvailableLabels] = useState([]);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);

  // Datasource & step
  const [datasources, setDatasources] = useState([]);
  const [step, setStep] = useState(
    panel.targets?.[0]?.step || editedPanel.targets?.[0]?.step || "60s"
  );

  // Fallback metrics when parent doesn't provide any
  const [localMetrics, setLocalMetrics] = useState([]);
  const metricsList = availableMetrics.length > 0 ? availableMetrics : localMetrics;

  // Query execution state
  const [isExecuting, setIsExecuting] = useState(false);
  const [queryError, setQueryError] = useState(null);

  // Load datasources and metrics fallback
  useEffect(() => {
    const load = async () => {
      try {
        const [dsRes, metricsRes] = await Promise.all([
          dashboardApi.getDatasources().catch(() => ({ success: false })),
          availableMetrics.length === 0 ? dashboardApi.searchMetrics("") : Promise.resolve(null),
        ]);
        if (dsRes?.success && dsRes?.data?.datasources) {
          setDatasources(dsRes.data.datasources);
        }
        if (metricsRes?.success && metricsRes?.data?.metrics && availableMetrics.length === 0) {
          setLocalMetrics(metricsRes.data.metrics);
        }
      } catch (e) {
        /* ignore */
      }
    };
    load();
  }, [availableMetrics.length]);

  // Sync query mode when opening with existing code
  useEffect(() => {
    const expr = panel.targets?.[0]?.expr;
    if (expr && expr.includes("(") && (expr.includes("rate") || expr.includes("sum") || expr.includes("avg"))) {
      setQueryMode("code");
    }
  }, [panel.targets]);

  // Load labels when metric changes
  useEffect(() => {
    const loadLabels = async () => {
      if (!selectedMetric) return;

      setIsLoadingLabels(true);
      try {
        const response = await dashboardApi.getMetricLabels(selectedMetric);
        if (response.success && response.data.labels) {
          setAvailableLabels(response.data.labels);
        }
      } catch (error) {
        console.error("Error loading labels:", error);
      } finally {
        setIsLoadingLabels(false);
      }
    };

    loadLabels();
  }, [selectedMetric]);

  const buildPromQLQuery = (filtersOverride) => {
    const f = filtersOverride ?? labelFilters;
    if (!selectedMetric) return "";
    const valid = f.filter((x) => x.label && x.value);
    if (valid.length === 0) return selectedMetric;
    const filterStr = valid.map((x) => `${x.label}${x.operator}"${x.value}"`).join(",");
    return `${selectedMetric}{${filterStr}}`;
  };

  // Execute query
  const handleExecuteQuery = async () => {
    setIsExecuting(true);
    setQueryError(null);

    try {
      let query =
        queryMode === "builder"
          ? buildPromQLQuery()
          : editedPanel.targets[0]?.expr;

      const panelWithVars = replacePanelVariables({ ...editedPanel, targets: [{ expr: query }] }, variables);
      query = panelWithVars.targets[0]?.expr;

      if (!query || !query.trim()) {
        message.warning("Please enter a query");
        setIsExecuting(false);
        return;
      }

      // Parse time range
      const now = Date.now();
      const start = parseGrafanaTimeToUnix(timeRange.from, now);
      const end = parseGrafanaTimeToUnix(timeRange.to, now);

      const ds = editedPanel.datasource;
      const datasourceId = (typeof ds === "object" && ds?.id) || ds?.uid || "default";

      const response = await dashboardApi.executeQuery({
        datasourceId,
        query,
        start,
        end,
        step,
      });

      if (response.success && response.data?.result) {
        // Transform data based on panel type
        const transformedData = transformPrometheusData(
          response.data.result,
          editedPanel.type,
        );

        // Update edited panel with new data
        setEditedPanel((prev) => ({
          ...prev,
          data: transformedData,
          loading: false,
          error: null,
        }));

        message.success("Query executed successfully!");
      } else {
        setQueryError("No data returned from query");
        message.warning("No data returned from query");
      }
    } catch (error) {
      console.error("Query execution error:", error);
      setQueryError(error.message || "Failed to execute query");
      message.error("Failed to execute query: " + error.message);
    } finally {
      setIsExecuting(false);
    }
  };

  // Update panel field
  const updateField = (field, value) => {
    setEditedPanel((prev) => ({ ...prev, [field]: value }));
  };

  // Update query expression
  const updateQueryExpr = (expr) => {
    setEditedPanel((prev) => ({
      ...prev,
      targets: [
        {
          ...prev.targets[0],
          expr: expr,
          refId: "A",
        },
      ],
    }));
  };

  // Label filter management
  const addLabelFilter = () => {
    setLabelFilters([...labelFilters, { label: "", operator: "=", value: "" }]);
  };

const updateLabelFilter = async (index, field, value) => {
  const newFilters = [...labelFilters];
  newFilters[index] = { ...newFilters[index], [field]: value };

  if (field === "label" && value && (newFilters[index].operator === "=" || newFilters[index].operator === "!=")) {
    newFilters[index].loadingValues = true;
    newFilters[index].values = [];
    setLabelFilters([...newFilters]);
    try {
      const response = await dashboardApi.getLabelValues(value);
      if (response.success && response.data?.values) {
        newFilters[index].values = response.data.values;
        newFilters[index].loadingValues = false;
        setLabelFilters([...newFilters]);
      }
    } catch {
      newFilters[index].loadingValues = false;
      setLabelFilters([...newFilters]);
    }
    if (queryMode === "builder") updateQueryExpr(buildPromQLQuery(newFilters));
    return;
  }

  if (field === "operator" && (value === "=" || value === "!=") && newFilters[index].label && !newFilters[index].values) {
    newFilters[index].loadingValues = true;
    setLabelFilters([...newFilters]);
    try {
      const response = await dashboardApi.getLabelValues(newFilters[index].label);
      if (response.success && response.data?.values) {
        newFilters[index].values = response.data.values;
        newFilters[index].loadingValues = false;
        setLabelFilters([...newFilters]);
      }
    } catch {
      newFilters[index].loadingValues = false;
      setLabelFilters([...newFilters]);
    }
    if (queryMode === "builder") updateQueryExpr(buildPromQLQuery(newFilters));
    return;
  }

  setLabelFilters(newFilters);
  if (queryMode === "builder") updateQueryExpr(buildPromQLQuery());
};


  const removeLabelFilter = (index) => {
    const newFilters = labelFilters.filter((_, i) => i !== index);
    setLabelFilters(newFilters);

    // Update query expression if in builder mode
    if (queryMode === "builder") {
      updateQueryExpr(buildPromQLQuery());
    }
  };

  // Save and close
  const handleSave = () => {
    if (queryMode === "builder") {
      updateQueryExpr(buildPromQLQuery());
    }
    const base = editedPanel.targets?.[0] || { refId: "A", expr: "", legendFormat: "" };
    const expr = queryMode === "builder" ? buildPromQLQuery() : (base.expr || "");
    const targets = [{
      ...base,
      expr,
      step,
      legendFormat: base.legendFormat ?? "",
    }];
    onSave({ ...editedPanel, targets });
    message.success("Panel saved!");
    onClose();
  };

  // Visualization type options
  const visualizationTypes = [
    { value: "timeseries", label: "Time Series" },
    { value: "gauge", label: "Gauge" },
    { value: "stat", label: "Stat" },
    { value: "pie", label: "Pie Chart" },
    { value: "table", label: "Table" },
  ];

  const operators = ["=", "!=", "=~", "!~"];

  return (
    <Layout style={{ height: "100vh", background: isDark ? "#0d1117" : "#f0f2f5" }}>
      {/* Header */}
      <div
        style={{
          padding: "12px 16px",
          background: isDark ? "#161b22" : "#fff",
          borderBottom: isDark ? "1px solid #30363d" : "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<CloseOutlined />}
            onClick={onClose}
            style={{
              background: isDark ? "#21262d" : undefined,
              border: isDark ? "1px solid #30363d" : undefined,
              color: isDark ? "#e6edf3" : undefined,
            }}
          >
            Back to Dashboard
          </Button>
          {/* <Text strong style={{ fontSize: '16px', color: '#fff', marginLeft: '12px' }}>
            {editedPanel.title}
          </Text> */}
        </Space>
        <Space>
          <Button
            type="primary"
            onClick={handleSave}
            style={{ background: "#52c41a", borderColor: "#52c41a" }}
          >
            Save & Close
          </Button>
        </Space>
      </div>

      {/* Main Content */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Left: Preview + Query Editor (70%) */}
        <div
          style={{
            flex: "0 0 70%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Preview Area (40%) */}
          <div
            style={{
              flex: "0 0 40%",
              padding: "16px",
              background: isDark ? "#0d1117" : "#fff",
              borderBottom: isDark ? "1px solid #30363d" : "1px solid #f0f0f0",
              overflow: "auto",
            }}
          >
            <div style={{ height: "100%", minHeight: "250px" }}>
              <PanelVisualization panel={editedPanel} />
            </div>
          </div>

          {/* Query Editor (60%) */}
          <div
            style={{
              flex: 1,
              padding: "16px",
              overflow: "auto",
              background: isDark ? "#0d1117" : "#f5f5f5",
            }}
          >
            <div
              style={{
                background: isDark ? "#161b22" : "#fff",
                padding: "16px",
                borderRadius: "8px",
                border: isDark ? "1px solid #30363d" : "1px solid #e8e8e8",
              }}
            >
              {/* Query Tabs */}
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  borderBottom: "1px solid #2d2d2d",
                  marginBottom: "16px",
                  paddingBottom: "8px",
                }}
              >
                <Button
                  type="text"
                  style={{
                    fontWeight: 500,
                    color: isDark ? "#e6edf3" : "#262626",
                    background: isDark ? "#21262d" : "#fafafa",
                    borderRadius: "8px 8px 0 0",
                    borderBottom: isDark ? "1px solid #30363d" : "1px solid #e8e8e8",
                  }}
                >
                  <span style={{ marginRight: "8px" }}>📊</span> Query
                </Button>
              </div>

              {/* Data source & Step */}
              <div style={{ marginBottom: "16px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
                {datasources.length > 0 && (
                  <div style={{ flex: "1", minWidth: 160 }}>
                    <Text style={{ display: "block", marginBottom: "4px", fontSize: "11px", color: isDark ? "#8b949e" : "#8c8c8c" }}>Data source</Text>
                    <Select
                      value={editedPanel.datasource?.id ?? editedPanel.datasource?.uid ?? datasources[0]?.id}
                      onChange={(id) => {
                        const ds = datasources.find((d) => d.id === id) || { id, uid: id, name: String(id) };
                        setEditedPanel((p) => ({ ...p, datasource: ds }));
                      }}
                      style={{ width: "100%" }}
                      size="small"
                      options={datasources.map((d) => ({ label: d.name || d.id, value: d.id }))}
                    />
                  </div>
                )}
                <div style={{ width: 100 }}>
                  <Text style={{ display: "block", marginBottom: "4px", fontSize: "11px", color: isDark ? "#8b949e" : "#8c8c8c" }}>Step</Text>
                  <Select
                    value={step}
                    onChange={setStep}
                    style={{ width: "100%" }}
                    size="small"
                    options={STEP_OPTIONS}
                  />
                </div>
              </div>

              {/* Mode Toggle */}
              <div
                style={{
                  marginBottom: "16px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <Button
                  type={queryMode === "builder" ? "primary" : "default"}
                  size="small"
                  onClick={() => setQueryMode("builder")}
                  style={{
                    background: queryMode === "builder" 
                      ? (isDark ? "#21262d" : "#e6f7ff") 
                      : "transparent",
                    color: queryMode === "builder" 
                      ? (isDark ? "#58a6ff" : "#1890ff") 
                      : (isDark ? "#8b949e" : "#8c8c8c"),
                    border: "1px solid #3d3d3d",
                  }}
                >
                  Builder
                </Button>
                <Button
                  type={queryMode === "code" ? "primary" : "default"}
                  size="small"
                  onClick={() => setQueryMode("code")}
                  style={{
                    background: queryMode === "code" 
                      ? (isDark ? "#21262d" : "#e6f7ff") 
                      : "transparent",
                    color: queryMode === "code" 
                      ? (isDark ? "#58a6ff" : "#1890ff") 
                      : (isDark ? "#8b949e" : "#8c8c8c"),
                    border: "1px solid #3d3d3d",
                  }}
                >
                  Code
                </Button>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleExecuteQuery}
                  loading={isExecuting}
                  style={{ marginLeft: "8px" }}
                >
                  Run Query
                </Button>
              </div>

              {/* Query Builder Mode */}
              {queryMode === "builder" && (
                <div>
                  {/* Metric Selector */}
                  <div style={{ marginBottom: "16px" }}>
                    <Text
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#b3b3b3",
                      }}
                    >
                      Metric
                    </Text>
                    <Select
                      value={selectedMetric}
                      onChange={(value) => {
                        setSelectedMetric(value);
                        updateQueryExpr(value);
                      }}
                      style={{ width: "100%" }}
                      placeholder="Select metric"
                      showSearch
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {metricsList.map((metric) => (
                        <Option key={metric} value={metric}>
                          {metric}
                        </Option>
                      ))}
                    </Select>
                  </div>

                  {/* Label Filters */}
                  <div style={{ marginBottom: "16px" }}>
                    <Text
                      style={{
                        display: "block",
                        marginBottom: "12px",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#b3b3b3",
                      }}
                    >
                      Label Filters
                    </Text>
                    {labelFilters.map((filter, index) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: "8px",
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <Select
                          value={filter.label}
                          onChange={(val) =>
                            updateLabelFilter(index, "label", val)
                          }
                          style={{ width: "180px" }}
                          placeholder="Label"
                          loading={isLoadingLabels}
                        >
                          {availableLabels.map((label) => (
                            <Option key={label} value={label}>
                              {label}
                            </Option>
                          ))}
                        </Select>
                        <Select
                          value={filter.operator}
                          onChange={(val) =>
                            updateLabelFilter(index, "operator", val)
                          }
                          style={{ width: "70px" }}
                        >
                          {operators.map((op) => (
                            <Option key={op} value={op}>
                              {op}
                            </Option>
                          ))}
                        </Select>
                        {filter.operator === "=~" ||
                        filter.operator === "!~" ? (
                          // Regex operators - keep text input
                          <Input
                            value={filter.value}
                            onChange={(e) =>
                              updateLabelFilter(index, "value", e.target.value)
                            }
                            placeholder="regex pattern"
                            style={{ flex: 1 }}
                          />
                        ) : (
                          // Equality operators - dropdown: variables first, then API values
                          <Select
                            value={filter.value}
                            onChange={(val) =>
                              updateLabelFilter(index, "value", val)
                            }
                            placeholder="Value or $variable"
                            style={{ flex: 1 }}
                            showSearch
                            loading={filter.loadingValues}
                            optionLabelProp="label"
                          >
                            {(variableDefinitions || []).length > 0 && (
                              <Select.OptGroup key="vars" label="Variables">
                                {(variableDefinitions || []).map((vd) => {
                                  const varRef = `$${vd.name}`;
                                  return (
                                    <Option key={varRef} value={varRef} label={varRef}>
                                      <span style={{ fontWeight: 500 }}>{varRef}</span>
                                      {vd.label ? (
                                        <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                                          ({vd.label})
                                        </Text>
                                      ) : null}
                                    </Option>
                                  );
                                })}
                              </Select.OptGroup>
                            )}
                            {(filter.values || []).length > 0 && (
                              <Select.OptGroup key="values" label="Label values">
                                {(filter.values || []).map((val) => (
                                  <Option key={val} value={val}>
                                    {val}
                                  </Option>
                                ))}
                              </Select.OptGroup>
                            )}
                          </Select>
                        )}
                        <Button
                          onClick={() => removeLabelFilter(index)}
                          icon={<DeleteOutlined />}
                          danger
                          type="text"
                        />
                      </div>
                    ))}
                    <Button
                      onClick={addLabelFilter}
                      icon={<PlusOutlined />}
                      style={{
                        width: "100%",
                        marginTop: "8px",
                        background: "transparent",
                        border: isDark ? "1px dashed #30363d" : "1px dashed #d9d9d9",
                        color: isDark ? "#8b949e" : "#8c8c8c",
                      }}
                    >
                      Add Label Filter
                    </Button>
                  </div>
                </div>
              )}

              {/* Code Mode */}
              {queryMode === "code" && (
                <div style={{ marginBottom: "16px" }}>
                  <Text
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#b3b3b3",
                    }}
                  >
                    PromQL Query
                  </Text>
                  <TextArea
                    value={editedPanel.targets?.[0]?.expr || ""}
                    onChange={(e) => updateQueryExpr(e.target.value)}
                    rows={6}
                    placeholder="Enter PromQL query..."
                    style={{
                      background: isDark ? "#0d1117" : "#fff",
                      border: isDark ? "1px solid #30363d" : "1px solid #d9d9d9",
                      color: isDark ? "#58a6ff" : "#1890ff",
                      fontFamily: "Monaco, Consolas, monospace",
                      fontSize: "13px",
                    }}
                  />
                </div>
              )}

              {/* Query Error */}
              {queryError && (
                <div
                  style={{
                    padding: "12px",
                    background: isDark ? "#2d1f1f" : "#fff2f0",
                    border: "1px solid #ff4d4f",
                    borderRadius: "8px",
                    color: "#ff4d4f",
                    fontSize: "12px",
                  }}
                >
                  {queryError}
                </div>
              )}

              {/* Generated Query Preview */}
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  background: isDark ? "#0d1117" : "#fafafa",
                  borderRadius: "8px",
                  border: isDark ? "1px solid #30363d" : "1px solid #e8e8e8",
                }}
              >
                <Text
                  style={{
                    fontSize: "11px",
                    color: "#8c8c8c",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Generated Query:
                </Text>
                <Text
                  style={{
                    fontSize: "12px",
                    fontFamily: "Monaco, Consolas, monospace",
                    color: "#6fb8ff",
                    wordBreak: "break-all",
                  }}
                >
                  {queryMode === "builder"
                    ? buildPromQLQuery()
                    : editedPanel.targets?.[0]?.expr || "No query"}
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Panel Options (30%) */}
        <div
          style={{
            flex: "0 0 30%",
            borderLeft: isDark ? "1px solid #30363d" : "1px solid #f0f0f0",
            background: isDark ? "#161b22" : "#fff",
            overflow: "auto",
            padding: "16px",
          }}
        >
          <Text
            strong
            style={{
              fontSize: "14px",
              color: isDark ? "#e6edf3" : "#262626",
              display: "block",
              marginBottom: "16px",
            }}
          >
            Panel Options
          </Text>

          {/* Panel Title */}
          <div style={{ marginBottom: "16px" }}>
            <Text
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: isDark ? "#8b949e" : "#8c8c8c",
                }}
              >
                Title
              </Text>
            <Input
              value={editedPanel.title}
              onChange={(e) => updateField("title", e.target.value)}
              placeholder="Panel title"
              style={{
                background: isDark ? "#0d1117" : "#fff",
                border: isDark ? "1px solid #30363d" : "1px solid #d9d9d9",
                color: isDark ? "#e6edf3" : undefined,
              }}
            />
          </div>

          {/* Panel Description */}
          <div style={{ marginBottom: "16px" }}>
            <Text
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: isDark ? "#8b949e" : "#8c8c8c",
                }}
              >
                Description
              </Text>
            <TextArea
              value={editedPanel.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Panel description"
              rows={2}
              style={{
                background: isDark ? "#0d1117" : "#fff",
                border: isDark ? "1px solid #30363d" : "1px solid #d9d9d9",
                color: isDark ? "#e6edf3" : undefined,
              }}
            />
          </div>

          <Divider style={{ margin: "16px 0", borderColor: "#2d2d2d" }} />

          {/* Visualization Type */}
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <span style={{ color: "#6fb8ff", fontSize: "16px" }}>📈</span>
              <Text
                style={{ fontSize: "12px", fontWeight: 600, color: isDark ? "#e6edf3" : "#262626" }}
              >
                Visualization
              </Text>
            </div>
            <Select
              value={editedPanel.type}
              onChange={(val) => updateField("type", val)}
              style={{ width: "100%" }}
            >
              {visualizationTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </div>

          <Divider style={{ margin: "16px 0", borderColor: "#2d2d2d" }} />

          {/* Standard Options */}
          <Text
            strong
            style={{
              fontSize: "12px",
              color: isDark ? "#e6edf3" : "#262626",
              display: "block",
              marginBottom: "12px",
            }}
          >
            Standard Options
          </Text>

          {/* Unit */}
          <div style={{ marginBottom: "16px" }}>
            <Text
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "12px",
                fontWeight: 500,
                    color: isDark ? "#8b949e" : "#8c8c8c",
                  }}
                >
                  Unit
            </Text>
            <Select
              value={editedPanel.fieldConfig?.defaults?.unit || "short"}
              onChange={(val) => {
                setEditedPanel((prev) => ({
                  ...prev,
                  fieldConfig: {
                    ...prev.fieldConfig,
                    defaults: {
                      ...prev.fieldConfig?.defaults,
                      unit: val,
                    },
                  },
                }));
              }}
              style={{ width: "100%" }}
            >
              <Option value="short">Short</Option>
              <Option value="percent">Percent (0-100)</Option>
              <Option value="percentunit" style={{ color: "black" }}>Percent (0.0-1.0)</Option>
              <Option value="bytes">Bytes</Option>
              <Option value="ms">Milliseconds</Option>
              <Option value="s">Seconds</Option>
              <Option value="Bps">Bytes/sec</Option>
              <Option value="decbytes">Decimal Bytes</Option>
              <Option value="ns">Nanoseconds</Option>
              <Option value="µs">Microseconds</Option>
            </Select>
          </div>

          {/* Min/Max */}
          <div style={{ marginBottom: "16px" }}>
            <Text
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "12px",
                fontWeight: 500,
                  color: isDark ? "#8b949e" : "#8c8c8c",
                }}
              >
                Min
            </Text>
            <Input
              type="number"
              value={editedPanel.fieldConfig?.defaults?.min || ""}
              onChange={(e) => {
                setEditedPanel((prev) => ({
                  ...prev,
                  fieldConfig: {
                    ...prev.fieldConfig,
                    defaults: {
                      ...prev.fieldConfig?.defaults,
                      min: e.target.value
                        ? parseFloat(e.target.value)
                        : undefined,
                    },
                  },
                }));
              }}
              placeholder="auto"
              style={{
                background: isDark ? "#0d1117" : "#fff",
                border: isDark ? "1px solid #30363d" : "1px solid #d9d9d9",
                color: isDark ? "#e6edf3" : undefined,
              }}
            />
          </div>

          {/* Decimals */}
          <div style={{ marginBottom: "16px" }}>
            <Text
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "12px",
                fontWeight: 500,
                  color: isDark ? "#8b949e" : "#8c8c8c",
                }}
              >
                Decimals
            </Text>
            <Input
              type="number"
              min={0}
              max={10}
              value={editedPanel.fieldConfig?.defaults?.decimals ?? ""}
              onChange={(e) => {
                setEditedPanel((prev) => ({
                  ...prev,
                  fieldConfig: {
                    ...prev.fieldConfig,
                    defaults: {
                      ...prev.fieldConfig?.defaults,
                      decimals: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    },
                  },
                }));
              }}
              placeholder="auto"
              style={{
                background: isDark ? "#0d1117" : "#fff",
                border: isDark ? "1px solid #30363d" : "1px solid #d9d9d9",
                color: isDark ? "#e6edf3" : undefined,
              }}
            />
          </div>

          <Divider style={{ margin: "16px 0", borderColor: "#2d2d2d" }} />

          {/* Thresholds */}
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "12px",
                cursor: "pointer",
              }}
              onClick={() => setShowThresholds(!showThresholds)}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span style={{ color: "#ff4d4f", fontSize: "16px" }}>🎯</span>
                <Text
                  style={{ fontSize: "12px", fontWeight: 600, color: isDark ? "#e6edf3" : "#262626" }}
                >
                  Thresholds
                </Text>
              </div>
              <Text style={{ color: isDark ? "#8b949e" : "#8c8c8c", fontSize: "12px" }}>
                {showThresholds ? "▼" : "▶"}
              </Text>
            </div>

            {showThresholds && (
              <ThresholdEditor
                thresholds={
                  editedPanel.fieldConfig?.defaults?.thresholds || {
                    mode: "absolute",
                    steps: [{ value: null, color: "green" }],
                  }
                }
                onChange={(newThresholds) => {
                  setEditedPanel((prev) => ({
                    ...prev,
                    fieldConfig: {
                      ...prev.fieldConfig,
                      defaults: {
                        ...prev.fieldConfig?.defaults,
                        thresholds: newThresholds,
                      },
                    },
                  }));
                }}
              />
            )}
          </div>

          <Divider style={{ margin: "16px 0", borderColor: "#2d2d2d" }} />

          {/* Legend Options */}
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <span style={{ color: "#52c41a", fontSize: "16px" }}>📊</span>
              <Text
                style={{ fontSize: "12px", fontWeight: 600, color: isDark ? "#e6edf3" : "#262626" }}
              >
                Legend
              </Text>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <Text
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: isDark ? "#8b949e" : "#8c8c8c",
                }}
              >
                Show Legend
              </Text>
              <Switch
                checked={editedPanel.options?.legend?.show !== false}
                onChange={(checked) => {
                  setEditedPanel((prev) => ({
                    ...prev,
                    options: {
                      ...prev.options,
                      legend: {
                        ...prev.options?.legend,
                        show: checked,
                      },
                    },
                  }));
                }}
              />
            </div>

            <div style={{ marginBottom: "12px" }}>
              <Text
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: isDark ? "#8b949e" : "#8c8c8c",
                }}
              >
                Placement
              </Text>
              <Select
                value={editedPanel.options?.legend?.placement || "bottom"}
                onChange={(val) => {
                  setEditedPanel((prev) => ({
                    ...prev,
                    options: {
                      ...prev.options,
                      legend: {
                        ...prev.options?.legend,
                        placement: val,
                      },
                    },
                  }));
                }}
                style={{ width: "100%" }}
                disabled={editedPanel.options?.legend?.show === false}
              >
                <Option value="bottom">Bottom</Option>
                <Option value="right">Right</Option>
                <Option value="top">Top</Option>
              </Select>
            </div>
            <div style={{ marginBottom: "12px" }}>
              <Text
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: isDark ? "#8b949e" : "#8c8c8c",
                }}
              >
                Legend format
              </Text>
              <Input
                value={editedPanel.targets?.[0]?.legendFormat ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setEditedPanel((prev) => {
                    const t0 = prev.targets?.[0] || { refId: "A", expr: "" };
                    return {
                      ...prev,
                      targets: [{ ...t0, legendFormat: val }],
                    };
                  });
                }}
                placeholder='e.g. {{label}} or {{job}}'
                style={{
                  background: isDark ? "#0d1117" : "#fff",
                  border: isDark ? "1px solid #30363d" : "1px solid #d9d9d9",
                  color: isDark ? "#e6edf3" : undefined,
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <Text
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "12px",
                fontWeight: 500,
                color: isDark ? "#8b949e" : "#8c8c8c",
              }}
            >
              Max
            </Text>
            <Input
              type="number"
              value={editedPanel.fieldConfig?.defaults?.max || ""}
              onChange={(e) => {
                setEditedPanel((prev) => ({
                  ...prev,
                  fieldConfig: {
                    ...prev.fieldConfig,
                    defaults: {
                      ...prev.fieldConfig?.defaults,
                      max: e.target.value
                        ? parseFloat(e.target.value)
                        : undefined,
                    },
                  },
                }));
              }}
              placeholder="auto"
              style={{
                background: isDark ? "#0d1117" : "#fff",
                border: isDark ? "1px solid #30363d" : "1px solid #d9d9d9",
                color: isDark ? "#e6edf3" : undefined,
              }}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PanelEditor;
